// @givmo/compliance — a question the form prints over several of its items, drawn as ONE row of the
// workbench (CCM-R1-DOCS-PRINTED-WEB; slice 2's frames 31, 32, 34 and 37, slice 3's 61, approved as
// shown).
//
// The left column is the form's own question with its number, as the catalog serves it
// (CCM-R1-FORM-CATALOG: COR-92's "14. Does your organization …? Enter yes or no for both.", the
// Annual Update's item 11). The right column is the group's items:
//
//   asked    each choice item's own label with the form's Yes and No beside it (frame 34), and,
//            where the form serves them, what an asked item carries beside its control ("Doesn't
//            apply", a route, "Same as", the not-required line); any other item the form now asks
//            (the firms table, once either is Yes) with its fields; any member's problem with its
//            value; then each distinct instruction its items carry, quoted once (Q2 (a));
//   answered one line, "Professional Solicitor: No · Fund-raising Counsel: No" (frames 31, 61),
//            Change for a writer, and who entered it.
//
// Each answer is still its own item's, through `answerFormItem` with that item's key: the group only
// draws them together. Every word is the form's own, the frames', or the item rows' approved lines.

import { Fragment, useState, type ReactNode } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { cn, testIdProp, toast } from '@givmo/design-system';
import type { ComplianceFormAnswerIn } from '@givmo/api-client';
import { useCompliance } from './compliance-context';
import { serverMessage } from './compliance-decode';
import {
  decodeFormPreparation,
  type FormGroup,
  type FormItem,
  type FormPreparation,
} from './compliance-preparation-decode';
import {
  CANCEL,
  CHANGE,
  NOT_ANSWERED_YET,
  answeredLine,
  groupSourceLines,
  groupValueLine,
  instructionLine,
  naLine,
  notRequiredLine,
  sourceLine,
  type SourceContext,
} from './compliance-preparation-model';
import { formPreparationQueryKey } from './compliance-queries';
import {
  AskItem,
  ITEM_PREFIX,
  OtherAnswers,
  SAVE_FAILED,
  SourceLine,
  YesNo,
  isYesNo,
  offersOtherAnswers,
  valueLines,
} from './filing-workbench-items';
import { QuietButton } from './filing-workbench-pages';

const PREFIX = 'compliance-workbench-group';

/** "14." → "14": the group's test id, from the form's own number. */
export function groupSlug(number: string): string {
  return number.replace(/[^0-9A-Za-z]+/g, '-').replace(/^-+|-+$/g, '') || 'question';
}

function isAsked(item: FormItem): boolean {
  return item.state === 'needs_input' || item.state === 'optional_empty';
}

function isTroubled(item: FormItem): boolean {
  return item.state === 'not_printable' || item.state === 'does_not_fit';
}

/** The quiet line under an item's value: who entered it, or the record it came from. */
function itemSourceLine(
  item: FormItem,
  context: SourceContext,
  personName: ((userId: string) => string | undefined) | undefined,
): string | undefined {
  if (item.answer !== undefined) return answeredLine(item, item.answer, personName, undefined);
  return item.sources[0] !== undefined ? sourceLine(item.sources[0], context) : undefined;
}

export interface WorkbenchGroupProps {
  group: FormGroup;
  /** The group's items the form asks now, in the group's order. */
  items: readonly FormItem[];
  preparation: FormPreparation;
  jurisdiction: string;
  editable: boolean;
  selected: boolean;
  onSelect: () => void;
  context: SourceContext;
  personName?: (userId: string) => string | undefined;
}

export function WorkbenchGroup({
  group,
  items,
  preparation,
  jurisdiction,
  editable,
  selected,
  onSelect,
  context,
  personName,
}: WorkbenchGroupProps) {
  const { orgRef, operations } = useCompliance();
  const queryClient = useQueryClient();
  const [editing, setEditing] = useState(false);
  const [refusal, setRefusal] = useState<string | null>(null);
  const answer = useMutation({
    mutationFn: ({ key, body }: { key: string; body: ComplianceFormAnswerIn }) =>
      operations.answerFormItem(orgRef ?? '', preparation.preparationId, key, body),
    onSuccess: (body, sent) => {
      if (decodeFormPreparation(body) !== undefined) {
        queryClient.setQueryData(formPreparationQueryKey(orgRef, preparation.preparationId), body);
      } else {
        void queryClient.invalidateQueries({
          queryKey: formPreparationQueryKey(orgRef, preparation.preparationId),
        });
      }
      setRefusal(null);
      setEditing(false);
      const answered = items.find((item) => item.key === sent.key);
      if (sent.body.kind === 'doesnt_apply' && answered?.doesntApply?.writes === 'N/A') {
        toast.success(naLine(jurisdiction));
      }
    },
    // An answer the form cannot take is refused with the server's own line, under the group.
    onError: (cause) => setRefusal(serverMessage(cause, SAVE_FAILED)),
  });
  const send = (key: string) => (body: ComplianceFormAnswerIn) => answer.mutate({ key, body });

  const slug = groupSlug(group.number);
  const testId = `${PREFIX}-${slug}`;
  const asking = editable && (editing || items.some((item) => isAsked(item) || isTroubled(item)));
  // A non-choice item's fields need the row's width, as a single item's do.
  const stacked =
    asking &&
    items.some((item) => !isYesNo(item) && (editing || isAsked(item) || isTroubled(item)));
  // Each distinct instruction the group's items carry, quoted once, in the items' order (Q2 (a)).
  const instructions = [
    ...new Set(items.flatMap((item) => (item.instruction ? [item.instruction.text] : []))),
  ];

  const question = (
    <p className="text-s13 leading-h15 text-text-secondary" {...testIdProp(`${testId}-label`)}>
      {group.number.length > 0 ? `${group.number} ${group.question.text}` : group.question.text}
    </p>
  );

  // The item a member's "Same as" copies, named as the workbench names it for a single item.
  const sameAsLabelOf = (item: FormItem) =>
    item.sameAs !== undefined
      ? preparation.items.find((other) => other.key === item.sameAs)?.label
      : undefined;
  // An asked member's problem (a value Givmo can't print, or that doesn't fit), as `WorkbenchItem`
  // draws it under an asked item, while the group isn't reopened with Change.
  const problemLine = (item: FormItem) =>
    isTroubled(item) && item.problem !== undefined && !editing ? (
      <SourceLine testId={`${ITEM_PREFIX}-${item.key}-problem`}>{item.problem}</SourceLine>
    ) : null;

  let right: ReactNode;
  if (asking) {
    right = (
      <div className="flex min-w-0 flex-col gap-8">
        {items.map((item) => {
          if (isYesNo(item)) {
            const sameAsLabel = sameAsLabelOf(item);
            const others = offersOtherAnswers(item, sameAsLabel);
            const optional = item.state === 'optional_empty';
            const problem = problemLine(item);
            const row = (
              <div
                className="flex flex-wrap items-center justify-between gap-x-12 gap-y-4"
                {...testIdProp(`${ITEM_PREFIX}-${item.key}`)}
              >
                <p className="min-w-0 text-s13 leading-h15 text-text">{item.label}</p>
                <YesNo item={item} pending={answer.isPending} onAnswer={send(item.key)} />
              </div>
            );
            // What an asked item carries beside its control — the form's other answers ("Doesn't
            // apply", a route, "Same as"), its not-required line, its problem — drawn only where
            // served, so a pair with none is its row alone (frame 34).
            if (!others && !optional && problem === null) {
              return <Fragment key={item.key}>{row}</Fragment>;
            }
            return (
              <div key={item.key} className="flex min-w-0 flex-col gap-6">
                {row}
                {others && (
                  <div className="flex flex-wrap items-center gap-x-16 gap-y-6">
                    <OtherAnswers item={item} sameAsLabel={sameAsLabel} onAnswer={send(item.key)} />
                  </div>
                )}
                {optional && <SourceLine>{notRequiredLine(jurisdiction)}</SourceLine>}
                {problem}
              </div>
            );
          }
          if (editing || isAsked(item) || isTroubled(item)) {
            return (
              <div
                key={item.key}
                className="flex min-w-0 flex-col gap-6"
                {...testIdProp(`${ITEM_PREFIX}-${item.key}`)}
              >
                <p className="text-s13 leading-h15 text-text">{item.label}</p>
                <AskItem
                  item={item}
                  jurisdiction={jurisdiction}
                  initial={editing || isTroubled(item) ? item.values : []}
                  pending={answer.isPending}
                  onAnswer={send(item.key)}
                  instruction={false}
                />
                {problemLine(item)}
              </div>
            );
          }
          return (
            <div
              key={item.key}
              className="flex min-w-0 flex-col gap-2"
              {...testIdProp(`${ITEM_PREFIX}-${item.key}`)}
            >
              <p className="text-s13 leading-h15 text-text">{item.label}</p>
              {valueLines(item).map((line, index) => (
                <p key={index} className="text-s14 leading-h15 text-text">
                  {line}
                </p>
              ))}
            </div>
          );
        })}
        {instructions.map((text) => (
          <SourceLine key={text} testId={`${testId}-instruction`}>
            {instructionLine(jurisdiction, text)}
          </SourceLine>
        ))}
        {editing && (
          <div>
            <QuietButton onClick={() => setEditing(false)} testId={`${testId}-cancel`}>
              {CANCEL}
            </QuietButton>
          </div>
        )}
      </div>
    );
  } else {
    const choices = items.filter(isYesNo);
    const others = items.filter((item) => !isYesNo(item));
    const value = groupValueLine(
      choices.map((item) => {
        const lines = valueLines(item);
        return {
          label: item.label,
          value: lines.length > 0 ? lines.join(' · ') : NOT_ANSWERED_YET,
        };
      }),
    );
    const sources = groupSourceLines(
      items.flatMap((item) => {
        const line = itemSourceLine(item, context, personName);
        return line !== undefined ? [{ label: item.label, line }] : [];
      }),
    );
    const changeable = editable && items.every((item) => item.kind !== 'unknown');
    right = (
      <div className="flex min-w-0 flex-col gap-2">
        {choices.length > 0 && (
          <div className="flex items-baseline justify-between gap-12">
            <p
              className="min-w-0 text-s14 leading-h15 text-text"
              {...testIdProp(`${testId}-value`)}
            >
              {value}
            </p>
            {changeable && (
              <QuietButton onClick={() => setEditing(true)} testId={`${testId}-change`}>
                {CHANGE}
              </QuietButton>
            )}
          </div>
        )}
        {others.map((item) => (
          <div key={item.key} className="flex min-w-0 flex-col gap-2 pt-4">
            <p className="text-s13 leading-h15 text-text-secondary">{item.label}</p>
            {(valueLines(item).length > 0 ? valueLines(item) : [NOT_ANSWERED_YET]).map(
              (line, index) => (
                <p key={index} className="text-s14 leading-h15 text-text">
                  {line}
                </p>
              ),
            )}
          </div>
        ))}
        {sources.map((line) => (
          <SourceLine key={line} testId={`${testId}-source`}>
            {line}
          </SourceLine>
        ))}
        {items.map((item) =>
          isTroubled(item) && item.problem !== undefined ? (
            <SourceLine key={item.key}>{item.problem}</SourceLine>
          ) : null,
        )}
      </div>
    );
  }

  return (
    <li
      className={cn(
        'px-20 py-12',
        stacked
          ? 'flex flex-col gap-6'
          : 'grid grid-cols-1 gap-x-20 gap-y-4 sm:grid-cols-[minmax(0,5fr)_minmax(0,7fr)]',
        selected && 'bg-brand-50/60',
      )}
      onClick={onSelect}
      onFocusCapture={onSelect}
      {...testIdProp(testId)}
    >
      {question}
      {right}
      {refusal !== null && (
        <p
          className="text-s12 leading-h15 text-text"
          role="alert"
          {...testIdProp(`${testId}-refusal`)}
        >
          {refusal}
        </p>
      )}
    </li>
  );
}
